import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';
import { de } from '@/i18n/de';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  /** `null` unterdrueckt die Fusszeile - fuer Dialoge, die eigene Aktionen mitbringen. */
  footer?: ReactNode | null;
  className?: string;
  /** Breiter Dialog (48rem) - fuer Tabellen wie Vorschau oder Import. */
  breit?: boolean;
}

/**
 * Setzt auf das native `<dialog>`-Element: Fokusfalle, Escape und der
 * Top-Layer kommen damit vom Browser statt aus eigenem Code.
 */
export function Dialog({
  open, onClose, title, description, children, footer, className, breit,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titelId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titelId}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
      className={cn(
        'm-auto rounded-xl border border-line',
        breit ? 'w-[min(48rem,calc(100vw-2rem))]' : 'w-[min(32rem,calc(100vw-2rem))]',
        'bg-surface p-0 text-ink shadow-xl backdrop:bg-black/40',
        className,
      )}
    >
      {open && (
        <div className="flex flex-col">
          <header className="border-b border-line px-5 py-4">
            <h2 id={titelId} className="text-base font-semibold">{title}</h2>
            {description && (
              <p className="mt-1 text-sm leading-relaxed text-ink-muted">{description}</p>
            )}
          </header>
          {children && <div className="px-5 py-4">{children}</div>}
          {footer !== null && (
            <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
              {footer ?? (
                <button
                  type="button"
                  onClick={onClose}
                  className="h-9 rounded-md px-3.5 text-sm text-ink-muted hover:text-ink"
                >
                  {de.common.close}
                </button>
              )}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}
